import { api } from './client'

/**
 * F-066 (TASK-0320, REQ-241-001): client di `LeadBulkOperationsController` (`/api/leads/bulk`). Le operazioni
 * multiple sui lead hanno prima un'anteprima (nulla viene scritto), poi l'esecuzione: sincrona (200) fino a
 * `syncThreshold` lead, in background oltre (202 con l'id del job da seguire con `useJobProgress`).
 */
export type LeadBulkOperation =
  | 'cambia_stato'
  | 'priorita'
  | 'aggiungi_campagna'
  | 'rimuovi_campagna'
  | 'pipeline'
  | 'contesta'

export const LEAD_BULK_OPERATION_LABELS: Record<LeadBulkOperation, string> = {
  cambia_stato: 'Cambia stato',
  priorita: 'Cambia priorità',
  aggiungi_campagna: 'Aggiungi a una campagna',
  rimuovi_campagna: 'Rimuovi da una campagna',
  pipeline: 'Sposta in una pipeline',
  contesta: 'Segna come contestato',
}

/** Un solo oggetto per tutte le operazioni: ognuna legge solo i propri campi. */
export interface LeadBulkParams {
  status?: string | undefined
  reason?: string | undefined
  note?: string | undefined
  priority?: string | undefined
  campaignId?: number | undefined
  pipelineId?: number | undefined
  stageId?: number | undefined
}

export interface LeadBulkRequest {
  operation: LeadBulkOperation
  params: LeadBulkParams
  leadIds: number[]
}

export type LeadBulkOutcome = 'Applicabile' | 'GiaApplicato' | 'TransizioneNonValida' | 'NonTrovato'

export interface LeadBulkPreview {
  operation: LeadBulkOperation
  total: number
  applicabile: number
  giaApplicato: number
  transizioneNonValida: number
  nonTrovato: number
  willRunInBackground: boolean
  syncThreshold: number
  items: { leadId: number; outcome: LeadBulkOutcome; detail: string | null }[]
}

export interface LeadBulkResult {
  operation: LeadBulkOperation
  total: number
  applied: number
  alreadyApplied: number
  invalidTransition: number
  notFound: number
  items: { leadId: number; outcome: string; detail: string | null }[]
}

export interface LeadBulkQueued {
  jobId: number
  queued: number
  notFound: number
}

export type LeadBulkExecution =
  | { kind: 'done'; result: LeadBulkResult }
  | { kind: 'queued'; queued: LeadBulkQueued }

export const leadBulkApi = {
  preview: (request: LeadBulkRequest) =>
    api<LeadBulkPreview>('/api/leads/bulk/preview', { method: 'POST', body: JSON.stringify(request) }),

  /**
   * 200 con l'esito per lead (eseguito nella richiesta) oppure 202 con `jobId` (in background): le due
   * risposte si distinguono dalla forma del corpo.
   */
  execute: async (request: LeadBulkRequest): Promise<LeadBulkExecution> => {
    const body = await api<LeadBulkResult | LeadBulkQueued>(
      '/api/leads/bulk/execute', { method: 'POST', body: JSON.stringify(request) })
    return 'jobId' in body ? { kind: 'queued', queued: body } : { kind: 'done', result: body }
  },
}
